import { FC } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { CommunityPerkDto, ShortCommunityPerkDto, UserDto, UserToMembershipDto } from '@sweatpals/shared';
import { getPublicFileUrl } from '@/modules/_core/utils/file.utils';
import {
	blurredPerkContent,
	getMembershipButton,
	membershipPromoteText,
	perkCardAvatar,
	perkCardButton,
	perkCardContent,
	perkCardDescription,
	perkCardHeader,
	perkCardRoot,
	perkCardTitle,
} from './perk-card.styles';
import { clsx, RainbowButton, Showable } from '@sweatpals/react-ui-kit';
import { LockedPerksAvatars } from './locked-perks.component';
import { TextWithReadMore } from '@/modules/_core/components/text-with-read-more/text-with-read-more.component';
import { createAndSanitizeMarkdown } from '@/modules/_core/utils/markdown.utils';

const shortTextLength = 84;

export interface PerkCardProps {
	data: CommunityPerkDto;
	hostData?: UserDto;
	currentUser?: UserDto;
	currentMembership?: UserToMembershipDto;
	perksWithMembership?: ShortCommunityPerkDto[];
	blur?: boolean;
	onGetMembership?(): void;
	onLogin?(): void;
}

export const PerkCard: FC<PerkCardProps> = props => {
	const { blur, data, currentMembership, perksWithMembership, currentUser, hostData, onGetMembership, onLogin } = props;

	const showBlurred = blur && !currentMembership && data.onlyForMemberships;

	return (
		<Box sx={perkCardRoot}>
			<Box sx={perkCardHeader}>
				<Box sx={perkCardAvatar(getPublicFileUrl(data.avatarId))}></Box>
				<Typography sx={perkCardTitle}>{data.name}</Typography>
			</Box>
			<Box sx={perkCardContent}>
				<Showable show={data.description}>
					<TextWithReadMore
						text={data.description}
						shortTextLength={shortTextLength}
						readMoreSx={{
							color: 'white',
							textDecoration: 'underline',
							fontFamily: 'Montserrat',
							fontSize: '13px',
							fontWeight: '700',
							lineHeight: '21px',
							cursor: 'pointer',
						}}
						sx={{
							flexGrow: 1,
						}}
						renderText={text => (
							<Box
								sx={clsx(perkCardDescription, { display: 'inline' })}
								dangerouslySetInnerHTML={
									text.length > shortTextLength
										? createAndSanitizeMarkdown(text ? `${text}...` : '')
										: createAndSanitizeMarkdown(text)
								}
							/>
						)}
					/>
				</Showable>
				<Showable show={data.url}>
					<Button disableRipple sx={perkCardButton} href={data.url} target='_blank' rel='noreferrer noopener'>
						View
					</Button>
				</Showable>
			</Box>

			<Showable show={showBlurred}>
				<Box sx={blurredPerkContent}>
					<Showable show={perksWithMembership?.length}>
						{() => <LockedPerksAvatars perks={perksWithMembership} />}
					</Showable>
					<Typography sx={membershipPromoteText}>Available only with membership</Typography>
					<RainbowButton
						disableRipple
						disableShadow
						sx={getMembershipButton}
						onClick={currentUser ? onGetMembership : onLogin}
					>
						{currentUser ? 'Get Membership' : 'Login'}
					</RainbowButton>
				</Box>
			</Showable>
		</Box>
	);
};
